/**
 * LoadGroup: one reveal per page, drawer or modal. Spec:
 * `brand-redesign-docs/10-SKELETONS.md`.
 *
 * Every data section inside a group reports its loading state with
 * `useLoadSlot(pending)` and renders its skeleton while the hook returns
 * true. The group holds every section in skeleton until all of them are
 * done, then reveals them together with a soft fade. While it waits, the
 * group's static chrome (headings, labels, buttons, chips, tabs, hero
 * panels) is painted as skeleton shapes by `loadGroup.css`, so the whole
 * surface is one skeleton at the exact geometry of the loaded page.
 *
 * - The reveal latches: refetches and later loads never re-skeleton the
 *   group. A section that mounts after the reveal shows its own skeleton.
 * - Safety valve: after `timeout` ms the group reveals everything that is
 *   ready; a slot still pending keeps only its own skeleton.
 * - Outside any group (`useLoadSlot` with no provider) a slot simply mirrors
 *   its own `pending` value.
 *
 * `pending` must be true while the section cannot show real content yet,
 * including a query that is waiting on another value (disabled until an id
 * arrives). A query that will never run for this viewer must pass false.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import "./loadGroup.css";

type Phase = "init" | "loading" | "revealed" | "idle";

interface LoadGroupContextValue {
  revealed: boolean;
  report: (id: string, pending: boolean) => void;
  unregister: (id: string) => void;
}

const LoadGroupContext = createContext<LoadGroupContextValue>({
  revealed: true,
  report: () => undefined,
  unregister: () => undefined,
});

export interface LoadGroupProps {
  children: ReactNode;
  /** `page` also makes the surface inert while it loads. Overlays stay
   *  interactive so their close button keeps working. */
  scope?: "page" | "overlay";
  /** Safety valve in ms. */
  timeout?: number;
}

export function LoadGroup({ children, scope = "page", timeout = 6000 }: LoadGroupProps) {
  const slots = useRef(new Map<string, boolean>());
  const [phase, setPhase] = useState<Phase>("init");

  const evaluate = useCallback(() => {
    const anyPending = [...slots.current.values()].some(Boolean);
    setPhase((p) => {
      if (p === "revealed" || p === "idle") return p;
      if (anyPending) return "loading";
      return p === "loading" ? "revealed" : "idle";
    });
  }, []);

  const report = useCallback(
    (id: string, pending: boolean) => {
      slots.current.set(id, pending);
      evaluate();
    },
    [evaluate],
  );

  const unregister = useCallback(
    (id: string) => {
      slots.current.delete(id);
      evaluate();
    },
    [evaluate],
  );

  // Children's layout effects run first, so by now every slot mounted in the
  // first pass has reported. A group with no slots settles to idle here,
  // before the browser paints.
  useLayoutEffect(() => {
    evaluate();
  }, [evaluate]);

  useEffect(() => {
    if (phase !== "loading") return;
    const t = window.setTimeout(() => setPhase("revealed"), timeout);
    return () => window.clearTimeout(t);
  }, [phase, timeout]);

  const revealed = phase === "revealed" || phase === "idle";
  const value = useMemo(() => ({ revealed, report, unregister }), [revealed, report, unregister]);
  const loading = phase === "loading" || phase === "init";

  return (
    <LoadGroupContext.Provider value={value}>
      <div
        className="lg"
        data-state={phase}
        data-scope={scope}
        aria-busy={loading || undefined}
        inert={scope === "page" && loading ? true : undefined}
      >
        {children}
      </div>
    </LoadGroupContext.Provider>
  );
}

/**
 * Registers a data section with the nearest `LoadGroup`. Returns true while
 * the section should render its skeleton.
 */
export function useLoadSlot(pending: boolean): boolean {
  const id = useId();
  const { revealed, report, unregister } = useContext(LoadGroupContext);

  useLayoutEffect(() => {
    report(id, pending);
  }, [id, pending, report]);

  useLayoutEffect(() => () => unregister(id), [id, unregister]);

  return !revealed || pending;
}

/** True until the nearest group has revealed. For chrome that has no data of
 *  its own but must hold a skeleton shape (rare; CSS covers most chrome). */
export function useLoadGroupPending(): boolean {
  return !useContext(LoadGroupContext).revealed;
}

/** Reports an overlay's own `loading` prop to the overlay's group. Renders
 *  nothing. Used by `Modal` and `SlidePanel`. */
export function OverlayLoadSlot({ pending }: { pending: boolean }) {
  useLoadSlot(pending);
  return null;
}
